import { type FC, useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { SearchField, type SearchFieldProps } from "widgets";
import { useDataAttributes } from "widgets/hooks";

import { parseFilterExpression } from "@/app-utils/filter.ts";

type SearchExprCb = (term: string, terms: string[]) => Promise<unknown>;

interface SearchWithExprParserProps
  extends Pick<SearchFieldProps, "width" | "label" | "placeholder" | "icon" | "autoFocus" | "name"> {
  controlledValue?: string;
  onSearchExprChange?: SearchExprCb;
}

export const AsyncSearchWithExprParser: FC<SearchWithExprParserProps> = (props) => {
  const [value, setValue] = useState(props.controlledValue ?? "");
  const [isLoading, setLoading] = useState(false);

  const dataAttributes = useDataAttributes(props);

  const debouncedChange = useDebouncedCallback(
    (value: string) => {
      setLoading(true);
      const terms = value ? parseFilterExpression(value) : [];
      void props.onSearchExprChange?.(value, terms).then(() => setLoading(false));
    },
    300,
    { leading: false, trailing: true },
  );

  useEffect(() => {
    return () => {
      // cleanup: cancel timeout
      debouncedChange.cancel();
    };
  }, []);

  useEffect(() => {
    debouncedChange.cancel();
    debouncedChange(value);
  }, [value]);

  useEffect(() => {
    if (props.controlledValue != null) {
      // compare to null because controlledValue is optional!
      setValue(props.controlledValue);
    }
  }, [props.controlledValue]);

  return (
    <SearchField
      value={value}
      onChange={setValue} // controlled value, need to be updated live
      onClear={() => {
        setValue(""); // controlled value, need to be updated live
      }}
      isLoading={isLoading}
      label={props.label}
      aria-label={props.label == null ? (props.placeholder ?? "Search") : "Search"}
      placeholder={props.placeholder}
      width={props.width}
      icon={props.icon ?? "search"}
      isRequired={true}
      autoFocus={props.autoFocus}
      name={props.name}
      {...dataAttributes}
    />
  );
};
